import {
  Center,
  Divider,
  Flex,
  Image,
  Link,
  Text,
  Heading,
  Tooltip,
  IconButton,
  Show,
  Hide,
  useDisclosure,
  Button,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { FaUser } from "react-icons/fa";
import { HiMenu } from "react-icons/hi";
import { IoMdLogOut, IoMdClose } from "react-icons/io";

import { useUser } from "../../context/UserContext";
import { useFeatureFlags } from "../../context/featureFlagsContext";
import DynamicLoginButton from "../DynamicLoginButton";

interface GithubUser {
  user?: {
    avatar_url?: string;
    login?: string;
  };
}

interface User {
  userId?: number;
  email?: string;
  github?: GithubUser;
}

interface UserAvatarProps {
  user: User;
}

interface NavigationLinksProps {
  loggedIn: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}

interface NavLink {
  path: string;
  label: string;
  requiresAuth: boolean;
}

const navigationLinks: NavLink[] = [
  {
    path: "/my-projects",
    label: "My Projects",
    requiresAuth: true,
  },
  {
    path: "/project-showcase",
    label: "Project Showcase",
    requiresAuth: false,
  },
];

interface NavLinkItemProps {
  link: NavLink;
  isMobile?: boolean;
  onClose?: () => void;
}

const NavLinkItem = ({ link, isMobile, onClose }: NavLinkItemProps) => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  return isMobile ? (
    <RouterLink
      to={link.path}
      style={{
        textDecoration: "none",
        fontSize: "xl",
        fontWeight: isActive(link.path) ? "600" : "400",
        display: "block",
        width: "100%",
        textAlign: "center",
        padding: "12px 0",
        color: isActive(link.path) ? "#3182CE" : "inherit",
      }}
      onClick={onClose}
    >
      {link.label}
    </RouterLink>
  ) : (
    <>
      <RouterLink
        to={link.path}
        style={{
          textDecoration: "none",
          fontSize: "md",
          fontWeight: isActive(link.path) ? "600" : "400",
          color: isActive(link.path) ? "#3182CE" : "inherit",
        }}
        onClick={onClose}
      >
        {link.label}
      </RouterLink>
      {link.path !== "/project-showcase" && (
        <Divider
          orientation="vertical"
          height="25px"
          marginX="12px"
          opacity={1}
          bg="rgba(0, 0, 0, 0.24)"
          width="1px"
        />
      )}
    </>
  );
};

const DesktopNavigation = ({ loggedIn }: NavigationLinksProps) => (
  <>
    {navigationLinks.map((link) =>
      !link.requiresAuth || (link.requiresAuth && loggedIn) ? (
        <NavLinkItem key={link.path} link={link} />
      ) : null,
    )}
  </>
);

const MobileNavigation = ({ loggedIn, onClose }: NavigationLinksProps) => (
  <>
    {navigationLinks.map((link) =>
      !link.requiresAuth || (link.requiresAuth && loggedIn) ? (
        <NavLinkItem key={link.path} link={link} isMobile onClose={onClose} />
      ) : null,
    )}
  </>
);

const NavigationLinks = ({
  loggedIn,
  onClose,
  isMobile = false,
}: NavigationLinksProps) =>
  isMobile ? (
    <MobileNavigation loggedIn={loggedIn} onClose={onClose} />
  ) : (
    <DesktopNavigation loggedIn={loggedIn} />
  );

const UserAvatar = ({ user }: UserAvatarProps) => (
  <>
    {user.github?.user?.avatar_url ? (
      <Image
        src={user.github.user.avatar_url}
        borderRadius="50%"
        height="30px"
        border="1px solid rgba(0, 0, 0, 0.16)"
        mr={1}
      />
    ) : (
      <Flex
        width="30px"
        height="30px"
        rounded="50%"
        bg="gray.300"
        alignItems="center"
        justifyContent="center"
        mr={1}
      >
        <FaUser size={14} color="white" />
      </Flex>
    )}
  </>
);

interface HeaderProps {
  logoutFn: () => void;
}

function Header({ logoutFn }: HeaderProps) {
  const { user } = useUser();
  const loggedIn = Boolean(user && user.email);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { featureFlags } = useFeatureFlags();
  const showTraditionalLogin = featureFlags?.SHOW_TRADITIONAL_LOGIN;

  return (
    <>
      <Flex
        backgroundColor="white"
        minWidth="max-content"
        alignItems="center"
        gap={{ base: "1", md: "4" }}
        paddingX={{ base: "16px", md: "20px" }}
        paddingY={{ base: "14px", md: "16px" }}
        marginBottom="20px"
        as="header"
        justifyContent="space-between"
        borderBottom="1px solid rgba(0, 0, 0, 0.12)"
        flexDirection="row"
      >
        <Flex
          alignItems="center"
          as="nav"
          width={{ base: "auto", md: "auto" }}
          justifyContent={{ base: "flex-start", md: "flex-start" }}
        >
          <Flex as={RouterLink} to="/" alignItems="center" mr={4}>
            <Image
              src="/earthfast-logo.svg"
              height="24px"
              verticalAlign="center"
            />
            <Heading
              verticalAlign={"center"}
              textDecor="none"
              fontSize="lg"
              fontWeight="600"
              marginLeft="10px"
              as="h1"
            >
              EarthFast
            </Heading>
          </Flex>
          <Hide below="md">
            <NavigationLinks loggedIn={loggedIn} isMobile={false} />
          </Hide>
        </Flex>

        <Show below="md">
          <Flex gap={2} alignItems="center">
            {loggedIn && user && (
              <Flex alignItems="center" gap={1}>
                <UserAvatar user={user} />
                <Text fontSize="sm" color="#01131F" noOfLines={1} maxW="120px">
                  {user.github?.user?.login
                    ? `@${user.github?.user?.login}`
                    : user.email}
                </Text>
              </Flex>
            )}
            <IconButton
              aria-label="Menu"
              icon={<HiMenu size={22} />}
              variant="ghost"
              onClick={onOpen}
              _hover={{ bg: "gray.100" }}
              _active={{ bg: "gray.200" }}
              size="sm"
            />
          </Flex>
        </Show>

        <Hide below="md">
          <Flex
            alignItems="center"
            width={{ base: "auto", md: "auto" }}
            justifyContent={{ base: "flex-end", md: "flex-end" }}
          >
            {loggedIn && user ? (
              <Flex
                border="1px solid rgba(0, 0, 0, 0.16)"
                py="4px"
                pl="4px"
                pr="14px"
                borderRadius="100px"
              >
                <UserAvatar user={user} />
                <Center>
                  <Tooltip
                    label={user.github?.user?.login ? user.email : undefined}
                    hasArrow
                  >
                    <Text fontSize="sm" color="#01131F">
                      {user.github?.user?.login
                        ? `@${user.github?.user?.login}`
                        : user.email}
                    </Text>
                  </Tooltip>
                  <Divider
                    orientation="vertical"
                    height="20px"
                    marginX="12px"
                    bg="rgba(0, 0, 0, 0.24)"
                    width="1px"
                  />
                  <Link onClick={logoutFn}>
                    <IoMdLogOut />
                  </Link>
                </Center>
              </Flex>
            ) : showTraditionalLogin ? (
              <Button
                as={RouterLink}
                to="/signin"
                variant="primary"
                size="md"
                px={6}
              >
                Log in or Sign up
              </Button>
            ) : (
              <DynamicLoginButton />
            )}
          </Flex>
        </Hide>
      </Flex>

      <Show below="md">
        <Flex
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="white"
          zIndex={20}
          flexDirection="column"
          visibility={isOpen ? "visible" : "hidden"}
          transform={isOpen ? "translateX(0)" : "translateX(100%)"}
          transition="all 0.3s ease-in-out"
          opacity={isOpen ? 1 : 0}
        >
          <Flex
            justify="space-between"
            align="center"
            px={4}
            py={4}
            borderBottom="1px solid"
            borderColor="gray.100"
            bg="white"
          >
            <Flex as={RouterLink} to="/" align="center" onClick={onClose}>
              <Image
                src="/earthfast-logo.svg"
                height="24px"
                verticalAlign="center"
              />
              <Heading
                color="black"
                fontSize="lg"
                fontWeight="600"
                marginLeft="10px"
              >
                EarthFast
              </Heading>
            </Flex>
            <IconButton
              aria-label="Close menu"
              icon={<IoMdClose size={24} />}
              variant="ghost"
              onClick={onClose}
              _hover={{ bg: "gray.100" }}
            />
          </Flex>

          <Flex flexDirection="column" pt={8} height="100%" bg="white">
            {loggedIn && user && (
              <Flex
                direction="column"
                align="center"
                pb={6}
                borderBottom="1px solid"
                borderColor="gray.100"
                mx={4}
              >
                <UserAvatar user={user} />
                <Text fontSize="md" fontWeight="500" mt={2} color="gray.700">
                  {user.github?.user?.login
                    ? `@${user.github?.user?.login}`
                    : user.email}
                </Text>
              </Flex>
            )}

            <Flex direction="column" pt={6} flex={1}>
              <NavigationLinks
                loggedIn={loggedIn}
                onClose={onClose}
                isMobile={true}
              />
            </Flex>

            <Flex
              justifyContent="center"
              borderTop="1px solid"
              borderColor="gray.100"
              py={6}
              px={4}
            >
              {loggedIn ? (
                <Text
                  onClick={() => {
                    onClose();
                    logoutFn();
                  }}
                  color="red.500"
                  fontSize="lg"
                  fontWeight="500"
                  textAlign="center"
                  py={2}
                  _hover={{ bg: "red.50", cursor: "pointer" }}
                >
                  Logout
                </Text>
              ) : showTraditionalLogin ? (
                <Button
                  as={RouterLink}
                  to="/signin"
                  onClick={onClose}
                  variant="primary"
                  width="full"
                  fontSize="lg"
                  fontWeight="bold"
                  py={2}
                  px={8}
                >
                  Log in or Sign up
                </Button>
              ) : (
                <DynamicLoginButton />
              )}
            </Flex>
          </Flex>
        </Flex>
      </Show>
    </>
  );
}

export default Header;
